import axios from 'axios';
import { clientConfig } from '../config/clientConfig';

/**
 * Centralized HTTP client.
 *
 * Responsibilities:
 *  - Single base URL (env-driven via clientConfig.apiBaseUrl).
 *  - Attaches `Authorization: Bearer <token>` from localStorage on every request.
 *  - On 401 responses to non-auth endpoints, clears the local session and
 *    broadcasts a global logout event so other tabs sign out too. The actual
 *    redirect is handled by App.js via the storage event listener.
 *
 * Usage:
 *  import { api } from '../api/client';
 *  const { data } = await api.get('/api/orders');
 */

import { clearAuthStorage, AUTH_TOKEN_KEY } from '../utils/authSession';

const LOGOUT_EVENT_KEY = 'app:globalLogout';

function clearLocalSession() {
  try {
    clearAuthStorage();
    sessionStorage.setItem(
      'sessionExpiredMessage',
      'Your session has expired. Please login again.'
    );
    localStorage.setItem(LOGOUT_EVENT_KEY, String(Date.now()));
  } catch {
    // ignore storage errors (private mode / quota)
  }
}

function isAuthEndpoint(url = '') {
  return url.includes('/api/auth/');
}

export const api = axios.create({
  baseURL: clientConfig.apiBaseUrl,
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (token) {
        config.headers = config.headers || {};
        // Don't override an explicitly-set Authorization (rare; keeps tests flexible).
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      if (clientConfig.apiVersion === 'v2') {
        config.headers = config.headers || {};
        if (!config.headers['X-Api-Version']) {
          config.headers['X-Api-Version'] = 'v2';
        }
      }
    } catch {
      // ignore
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    const d = response.data;
    if (
      d &&
      typeof d === 'object' &&
      d.success === true &&
      d.apiVersion === 'v2' &&
      Object.prototype.hasOwnProperty.call(d, 'data')
    ) {
      return { ...response, data: d.data };
    }
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    if (status === 401 && !isAuthEndpoint(url)) {
      clearLocalSession();
      // Same-tab: storage listener does not fire for own writes; use a dedicated event.
      try {
        window.dispatchEvent(new Event('app:session-invalid'));
      } catch {
        try {
          window.location.assign('/login');
        } catch {
          /* noop */
        }
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Extract a user-facing error message from a thrown axios error.
 * Backend returns one of: { error: '...' }, { message: '...' },
 * { errorCode, message }, or a plain string.
 */
export function extractErrorMessage(err, fallback = 'Something went wrong') {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === 'string') return data;
  if (data.success === false && data.error && (data.error.message || data.error.code)) {
    return data.error.message || data.error.code || fallback;
  }
  if (data.error && typeof data.error === 'string') return data.error;
  return data.message || data.error || fallback;
}
